import { ReferenceRangeDisplay } from "@/components/ReferenceRangeDisplay/ReferenceRangeDisplay";
import { Badge, BodyText, Button, Separator, Tooltip } from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import {
  LifestyleQuestionnaireTypes,
  MentalHealthQuestionnaireTypes,
  QuestionnaireLifeCycleStatus,
  type QuestionnaireResponseData,
} from "@/types/questionnaires";
import { QuestionnaireResponse } from "@curai/js-fhir-tools";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  ClipboardPenLine,
  EllipsisVertical,
  RotateCcwClock,
} from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import {
  QUESTIONNAIRE_REASON_LABELS,
  QuestionnaireActionType,
  useQuestionnaireAvailableActions,
} from "../network";
import { formatAuthoredDate, formatQuestionnaireScore } from "../utils";
import { QuestionnaireHistoryDetailDialog } from "./QuestionnaireDetailDialog/QuestionnaireHistoryDetailDialog";
import { QuestionnaireHistoryModal } from "./QuestionnaireHistoryModal/QuestionnaireHistoryModal";

const TRACKER_UUID_BY_QUESTIONNAIRE_ID: Record<
  LifestyleQuestionnaireTypes | MentalHealthQuestionnaireTypes,
  string
> = {
  [LifestyleQuestionnaireTypes.GLTEQ]:
    TRACKERS.VIEW_PHYSICAL_ACTIVITY_QUESTIONNAIRE_RESPONSES_IN_HEALTH_MAINTENANCE,
  [LifestyleQuestionnaireTypes.AUDIT_C]:
    TRACKERS.VIEW_ALCOHOL_QUESTIONNAIRE_RESPONSES_IN_HEALTH_MAINTENANCE,
  [LifestyleQuestionnaireTypes.FTND]:
    TRACKERS.VIEW_TOBACCO_QUESTIONNAIRE_RESPONSES_IN_HEALTH_MAINTENANCE,
  [LifestyleQuestionnaireTypes.PSQI]:
    TRACKERS.VIEW_SLEEP_QUESTIONNAIRE_RESPONSES_IN_HEALTH_MAINTENANCE,
  [LifestyleQuestionnaireTypes.REAP_S]:
    TRACKERS.VIEW_DIET_QUESTIONNAIRE_RESPONSES_IN_HEALTH_MAINTENANCE,
  [MentalHealthQuestionnaireTypes.PHQ_9]:
    TRACKERS.VIEW_DEPRESSION_QUESTIONNAIRE_RESPONSES_IN_HEALTH_MAINTENANCE,
  [MentalHealthQuestionnaireTypes.PHQ_2]:
    TRACKERS.VIEW_DEPRESSION_QUESTIONNAIRE_RESPONSES_IN_HEALTH_MAINTENANCE,
  [MentalHealthQuestionnaireTypes.GAD_7]:
    TRACKERS.VIEW_ANXIETY_QUESTIONNAIRE_RESPONSES_IN_HEALTH_MAINTENANCE,
  [MentalHealthQuestionnaireTypes.GAD_2]:
    TRACKERS.VIEW_ANXIETY_QUESTIONNAIRE_RESPONSES_IN_HEALTH_MAINTENANCE,
};

interface QuestionnaireTableRowProps extends QuestionnaireResponseData {
  allQuestionnaires: QuestionnaireResponseData[];
  onSend: (
    questionnaireType:
      | LifestyleQuestionnaireTypes
      | MentalHealthQuestionnaireTypes,
    questionnaireName: string,
    patientId: string,
  ) => void;
  isSending: boolean;
  openQuestionnaire?: string;
  onAutoOpenComplete?: () => void;
}

export const QuestionnaireTableRow = ({
  questionnaireId,
  questionnaireShortLabel,
  questionnaireResponse,
  responseScore,
  maxScore,
  questionnaireLifecycleStatus,
  questionnaireSentDatetime,
  questionnaireStatusTransitionDatetime,
  onSend,
  isSending,
  openQuestionnaire,
  onAutoOpenComplete,
  criticality,
  referenceRanges,
}: QuestionnaireTableRowProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const {
    questionnaireResponseId: questionnaireResponseIdFromUrl,
    questionnaireType: questionnaireTypeFromUrl,
  } = useSearch({ strict: false });
  const navigate = useNavigate();

  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedQuestionnaireResponseId, setSelectedQuestionnaireResponseId] =
    useState<string>(questionnaireResponse?.id || "");
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const isCompleted = questionnaireResponse?.status === "completed";

  const { data: availableActions, isError: isAvailableActionsError } =
    useQuestionnaireAvailableActions(patientId, questionnaireId);

  const sendQuestionnaireAction =
    availableActions?.[QuestionnaireActionType.SEND_QUESTIONNAIRE];
  const isSendDisabled =
    !isAvailableActionsError &&
    Boolean(sendQuestionnaireAction?.unavailableReason);
  const sendDisabledReason =
    sendQuestionnaireAction?.unavailableReason !== undefined
      ? QUESTIONNAIRE_REASON_LABELS[sendQuestionnaireAction.unavailableReason]
      : undefined;

  const getStatusDisplay = (shortDateFormat = true) => {
    if (questionnaireLifecycleStatus) {
      const date = formatAuthoredDate(
        questionnaireStatusTransitionDatetime || undefined,
        shortDateFormat,
        false,
      );
      switch (questionnaireLifecycleStatus) {
        case QuestionnaireLifeCycleStatus.COMPLETED:
          return `Completed ${date}`;
        case QuestionnaireLifeCycleStatus.IN_PROGRESS:
          return `In Progress ${date}`;
        case QuestionnaireLifeCycleStatus.SENT:
          return `Sent ${date}`;
        default:
          return "--";
      }
    }
    // No complete-questionnaire task means the clinician didn't send it (e.g. the patient
    // started it on their own), so fall back to the response's own status.
    if (questionnaireResponse) {
      const authoredDate = questionnaireResponse.authored
        ? ` ${formatAuthoredDate(questionnaireResponse.authored, shortDateFormat, false)}`
        : "";
      switch (questionnaireResponse.status) {
        case "completed":
          return `Completed${authoredDate}`;
        case "in-progress":
          return "In Progress";
        case "stopped":
          // Intake stops a response when its session expires or is replaced before the patient finishes
          return "Incomplete";
        default:
          return "--";
      }
    }

    return "--";
  };

  const isReviewed = questionnaireResponse
    ? (() => {
        const qr = new QuestionnaireResponse(questionnaireResponse);
        return (
          qr.getConfirmationActorName() !== undefined &&
          qr.getConfirmationOccurredDate() !== undefined
        );
      })()
    : false;

  const handleOpenDetailDialog = (open: boolean) => {
    setIsDetailDialogOpen(open);
    if (open) {
      setSelectedQuestionnaireResponseId(questionnaireResponse?.id || "");
      trackEvent(TRACKER_UUID_BY_QUESTIONNAIRE_ID[questionnaireId]);
    }
  };

  const handleOpenHistoryDialog = () => {
    setIsHistoryDialogOpen(true);
  };

  // If the URL has a questionnaireResponseId and its type matches this row's type, open this dialog
  useEffect(() => {
    if (
      questionnaireResponseIdFromUrl &&
      questionnaireTypeFromUrl &&
      questionnaireTypeFromUrl === questionnaireId
    ) {
      setSelectedQuestionnaireResponseId(questionnaireResponseIdFromUrl);
      setIsDetailDialogOpen(true);
      trackEvent(TRACKER_UUID_BY_QUESTIONNAIRE_ID[questionnaireId]);

      // Clear the URL params so they don't interfere with selecting other questionnaires
      navigate({
        to: ".",
        search: (prev) => {
          const {
            questionnaireResponseId: _qrId,
            questionnaireType: _qType,
            ...rest
          } = prev;
          return rest;
        },
        replace: true,
      });
    }
  }, [
    questionnaireResponseIdFromUrl,
    questionnaireTypeFromUrl,
    questionnaireId,
    navigate,
  ]);

  useEffect(() => {
    if (openQuestionnaire === questionnaireId) {
      if (
        questionnaireResponseIdFromUrl &&
        questionnaireTypeFromUrl &&
        questionnaireTypeFromUrl === questionnaireId
      ) {
        return; // Let the URL effecthandle it
      }
      setSelectedQuestionnaireResponseId(questionnaireResponse?.id || "");
      setIsDetailDialogOpen(true);
      trackEvent(TRACKER_UUID_BY_QUESTIONNAIRE_ID[questionnaireId]);
      onAutoOpenComplete?.();
    }
  }, [
    openQuestionnaire,
    questionnaireResponse,
    questionnaireId,
    questionnaireResponseIdFromUrl,
    questionnaireTypeFromUrl,
    onAutoOpenComplete,
  ]);

  return (
    <Fragment>
      <div
        className="pl-3 min-h-8 flex justify-between items-top"
        data-testid={`questionnaire-row-${questionnaireId}`}
      >
        {/* Type Column */}
        <BodyText
          size="sm"
          className="w-[30%] pt-2"
          title={questionnaireShortLabel}
        >
          {questionnaireShortLabel}
        </BodyText>

        {/* Score Column */}
        <div className="w-[15%] pt-0.5">
          {!questionnaireResponse ? (
            <BodyText size="sm">--</BodyText>
          ) : questionnaireLifecycleStatus ===
              QuestionnaireLifeCycleStatus.SENT || !isCompleted ? (
            <BodyText size="sm">--</BodyText>
          ) : (
            <Button
              variant="link"
              size="sm"
              className="font-normal"
              onClick={() => handleOpenDetailDialog(true)}
            >
              <ReferenceRangeDisplay
                criticality={criticality ?? null}
                data={formatQuestionnaireScore(responseScore, maxScore)}
                dataClassName="underline decoration-1 underline-offset-2"
                referenceRanges={referenceRanges ?? null}
                tooltipFallback="See Results"
              />
            </Button>
          )}
        </div>

        {/* Assigned Column */}
        <BodyText
          size="sm"
          className="w-[25%] pt-2 pr-2"
          title={questionnaireResponse?.authored}
        >
          {formatAuthoredDate(
            questionnaireSentDatetime || undefined,
            false,
            false,
          )}
        </BodyText>

        {/* Status Column */}
        <div className="w-[30%] flex items-top justify-between gap-2">
          <div className="flex flex-col gap-1 pt-2 pb-1">
            <BodyText size="sm">{getStatusDisplay(false)}</BodyText>
            {questionnaireResponse && isCompleted && !isReviewed && (
              <Badge variant="warning" className="w-fit">
                <BodyText textColor="warning" size="sm">
                  Not Reviewed
                </BodyText>
              </Badge>
            )}
          </div>
          {/* Action Column */}
          <DropdownMenu>
            <Tooltip
              tooltipContentText="Options"
              side="bottom"
              triggerClassName="leading-[0px] pt-2 h-6"
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="icon"
                  size="sm"
                  aria-label={`Options for ${questionnaireShortLabel}`}
                  className="m-0 p-0 h-4"
                >
                  <EllipsisVertical className="text-link" size={18} />
                </Button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent align="start">
              <Tooltip
                tooltipContentText={
                  isSendDisabled && sendDisabledReason
                    ? sendDisabledReason
                    : undefined
                }
                side="right"
              >
                <DropdownMenuItem
                  onClick={() => {
                    trackEvent(TRACKERS.SEND_NEW_QUESTIONNAIRE);
                    onSend(questionnaireId, questionnaireShortLabel, patientId);
                  }}
                  disabled={isSending || isSendDisabled}
                  title={
                    isSendDisabled && sendDisabledReason
                      ? sendDisabledReason
                      : undefined
                  }
                >
                  <ClipboardPenLine className="h-4 w-4 mr-2" />
                  Send Questionnaire
                </DropdownMenuItem>
              </Tooltip>
              <DropdownMenuItem
                onClick={() => {
                  trackEvent(TRACKERS.VIEW_QUESTIONNAIRE_RESPONSE_HISTORY);
                  handleOpenHistoryDialog();
                }}
              >
                <RotateCcwClock className="h-4 w-4 mr-2" />
                See History
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <QuestionnaireHistoryDetailDialog
        selectedResponseId={selectedQuestionnaireResponseId}
        onSelectedResponseIdChange={setSelectedQuestionnaireResponseId}
        questionnaireType={questionnaireId}
        patientId={patientId}
        open={isDetailDialogOpen}
        onOpenChange={handleOpenDetailDialog}
      />
      <Separator />
      {/* History Modal */}
      <QuestionnaireHistoryModal
        open={isHistoryDialogOpen}
        onOpenChange={setIsHistoryDialogOpen}
        patientId={patientId}
        questionnaireType={questionnaireId}
        questionnaireDisplayName={questionnaireShortLabel}
        onSelectResponse={(responseId) => {
          setSelectedQuestionnaireResponseId(responseId);
          setIsDetailDialogOpen(true);
        }}
      />
    </Fragment>
  );
};
